/* AdminPromoDashboard.jsx */
import React, { useEffect, useState, useCallback } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  where,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "./firebase";
import PromoSpot from "./components/common/PromoSpot";

const tokens = {
  bg: "#f3f4f6",
  cardBg: "#ffffff",
  cardBorder: "#e5e7eb",
  text: "#111827",
  textSubtle: "#6b7280",
  blue: "#0a66c2",
  radius: 12,
  maxw: 1120,
  shadow: "0 10px 30px rgba(15,23,42,0.08)",
};

const emptyForm = {
  topLabel: "MIKDOK FOKUS",
  title: "",
  subtitle: "",
  description: "",
  ctaLabel: "",
  ctaPath: "",
  externalUrl: "",
  targetType: "internal", // "internal" | "external"
  slot: "doctor-home-main",
  priority: 10,
  validFrom: "",
  validTo: "",
  active: true,
   imageUrl: "",   
  audiences: {
    doctor: true,
    patient: false,
  },
};

const AdminPromoDashboard = () => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [promos, setPromos] = useState([]);
  const [selectedPromoId, setSelectedPromoId] = useState(null);
  const [statsByPromo, setStatsByPromo] = useState({});
  const [loadingPromos, setLoadingPromos] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);

  // ─────────────────────────────────────────
  // Load existing promos (last 50)
  // ─────────────────────────────────────────
  // ─────────────────────────────────────────
  // Load existing promos (simple, no orderBy first)
  // ─────────────────────────────────────────
  const loadPromos = useCallback(async () => {
    setLoadingPromos(true);
    try {
      const colRef = collection(db, "promoSlots");
      const snap = await getDocs(colRef);

      console.log("[AdminPromoDashboard] promoSlots docs:", snap.size);
      const list = snap.docs.map((d) => {
        const data = d.data();
        console.log("[AdminPromoDashboard] promo doc", d.id, data);
        return { id: d.id, ...data };
      });

      // Optional: sort by createdAt (if exists) and then by priority in JS
      list.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() ?? 0;
        const bTime = b.createdAt?.toMillis?.() ?? 0;
        if (bTime !== aTime) return bTime - aTime;
        const aPr = typeof a.priority === "number" ? a.priority : 10;
        const bPr = typeof b.priority === "number" ? b.priority : 10;
        return aPr - bPr;
      });

      setPromos(list);
    } catch (err) {
      console.error("Failed to load promos", err);
      setError("Failed to load promos.");
    } finally {
      setLoadingPromos(false);
    }
  }, []);

  // ─────────────────────────────────────────
  // Aggregate stats from promoEvents
  // ─────────────────────────────────────────
  const loadStatsForPromos = useCallback(
    async (promoList) => {
      if (!promoList || promoList.length === 0) {
        setStatsByPromo({});
        return;
      }
      setLoadingStats(true);
      try {
        const result = {};
        for (const p of promoList) {
          const eventsRef = collection(db, "promoEvents");

          const qImpressions = query(
            eventsRef,
            where("promoId", "==", p.id),
            where("type", "==", "impression")
          );
          const qViews = query(
            eventsRef,
            where("promoId", "==", p.id),
            where("type", "==", "view")
          );
          const qClicks = query(
            eventsRef,
            where("promoId", "==", p.id),
            where("type", "==", "click")
          );

          const [cImpr, cView, cClick] = await Promise.all([
            getCountFromServer(qImpressions),
            getCountFromServer(qViews),
            getCountFromServer(qClicks),
          ]);

          const impressions = cImpr.data().count || 0;
          const views = cView.data().count || 0;
          const clicks = cClick.data().count || 0;
          const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
          result[p.id] = {
            impressions,
            views,
            clicks,
            ctr,
          };
        }
        setStatsByPromo(result);
      } catch (err) {
        console.error("Failed to load promo stats", err);
      } finally {
        setLoadingStats(false);
      }
    },
    []
  );

  useEffect(() => {
    loadPromos();
  }, [loadPromos]);

  useEffect(() => {
    if (promos.length > 0) {
      loadStatsForPromos(promos);
    } else {
      setStatsByPromo({});
    }
  }, [promos, loadStatsForPromos]);

  // ─────────────────────────────────────────
  // Form handlers
  // ─────────────────────────────────────────
  const onFieldChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const onAudienceToggle = (audKey) => {
    setForm((f) => ({
      ...f,
      audiences: {
        ...f.audiences,
        [audKey]: !f.audiences[audKey],
      },
    }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setSelectedPromoId(null);
    setError("");
  };

const loadPromoIntoForm = (promo) => {
  setSelectedPromoId(promo.id);
  setForm({
    topLabel: promo.topLabel || "MIKDOK FOKUS",
    title: promo.title || "",
    subtitle: promo.subtitle || "",
    description: promo.description || "",
    ctaLabel: promo.ctaLabel || "",
    ctaPath: promo.ctaPath || "",
    externalUrl: promo.externalUrl || "",
    targetType: promo.targetType || (promo.externalUrl ? "external" : "internal"),
    slot: promo.slot || "doctor-home-main",
    priority: typeof promo.priority === "number" ? promo.priority : 10,
    validFrom: promo.validFrom || "",
    validTo: promo.validTo || "",
    active: promo.active !== false,
    imageUrl: promo.imageUrl || "",   // 👈 NEW
    audiences: {
      doctor: promo.audience === "doctor",
      patient: promo.audience === "patient",
    },
  });
};


  // ─────────────────────────────────────────
  // Save / publish promo
  // ─────────────────────────────────────────
  const handleSavePromo = async () => {
    setError("");

    if (!form.title || !form.subtitle || !form.ctaLabel) {
      setError("Title, subtitle and CTA label are required.");
      return;
    }

    const selectedAudiences = Object.entries(form.audiences)
      .filter(([, v]) => v)
      .map(([k]) => k);

    if (!selectedAudiences.length && !selectedPromoId) {
      setError("Select at least one audience (doctor / patient).");
      return;
    }

    setSaving(true);
    try {
      if (selectedPromoId) {
        // Update single existing promo (it has one audience)
        const promoRef = doc(db, "promoSlots", selectedPromoId);
        await updateDoc(promoRef, {
  topLabel: form.topLabel || "MIKDOK FOKUS",
  title: form.title,
  subtitle: form.subtitle,
  description: form.description,
  ctaLabel: form.ctaLabel,
  ctaPath: form.ctaPath || "",
  externalUrl: form.externalUrl || "",
  targetType: form.targetType,
  slot: form.slot,
  priority: Number(form.priority) || 10,
  validFrom: form.validFrom || "",
  validTo: form.validTo || "",
  active: !!form.active,
  imageUrl: form.imageUrl || "",      // 👈 NEW
  updatedAt: serverTimestamp(),
});

      } else {
        // Create NEW promo(s). If multiple audiences checked, we create one doc per audience.
        const base = {
  topLabel: form.topLabel || "MIKDOK FOKUS",
  title: form.title,
  subtitle: form.subtitle,
  description: form.description,
  ctaLabel: form.ctaLabel,
  ctaPath: form.ctaPath || "",
  externalUrl: form.externalUrl || "",
  targetType: form.targetType,
  slot: form.slot,
  priority: Number(form.priority) || 10,
  validFrom: form.validFrom || "",
  validTo: form.validTo || "",
  active: !!form.active,
  imageUrl: form.imageUrl || "",      // 👈 NEW
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
};


        for (const aud of selectedAudiences) {
          await addDoc(collection(db, "promoSlots"), {
            ...base,
            audience: aud, // "doctor" or "patient"
          });
        }
      }

      await loadPromos();
      resetForm();
    } catch (err) {
      console.error("Failed to save promo", err);
      setError("Failed to save promo.");
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────
  // Preview object for PromoSpot
  // ─────────────────────────────────────────
const previewPromo = {
  id: selectedPromoId || "preview",
  topLabel: form.topLabel || "MIKDOK FOKUS",
  title: form.title || "Promo title",
  subtitle: form.subtitle || "Short subtitle explaining your offer.",
  description:
    form.description ||
    "Optional longer description about the campaign, targeted doctors/patients, etc.",
  ctaLabel: form.ctaLabel || "Learn more",
  ctaPath: form.ctaPath,
  externalUrl: form.externalUrl,
  targetType: form.targetType,
  slot: form.slot,
  active: form.active,
  imageUrl:
    form.imageUrl ||
    "https://via.placeholder.com/300x100/0f172a/ffffff?text=MikDok+Promo", // 👈 fallback
};


  // Dummy handlers for preview (no tracking here)
  const handlePreviewClick = () => {};
  const handlePreviewExpand = () => {};
  const handlePreviewClickTrack = () => {};

  return (
    <div
      style={{
        minHeight: "100vh",
        background: tokens.bg,
        padding: 24,
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: tokens.maxw,
          margin: "0 auto",
        }}
      >
        {/* Header */}
        <header
          style={{
            marginBottom: 20,
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                color: tokens.text,
              }}
            >
              Promo Console
            </h1>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 13,
                fontWeight: 500,
                color: tokens.textSubtle,
              }}
            >
              Create, preview and publish MikDok promos. Track impressions,
              views and clicks per slot.
            </p>
          </div>
        </header>

        {error && (
          <div
            style={{
              marginBottom: 16,
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid #fecaca",
              background: "#fef2f2",
              color: "#b91c1c",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}

        {/* Layout: form + preview */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)",
            gap: 16,
            alignItems: "flex-start",
            marginBottom: 24,
          }}
        >
          {/* Form card */}
          <section
            style={{
              background: tokens.cardBg,
              borderRadius: tokens.radius,
              border: `1px solid ${tokens.cardBorder}`,
              boxShadow: tokens.shadow,
              padding: 16,
            }}
          >
            <div
              style={{
                marginBottom: 10,
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: tokens.text,
                  }}
                >
                  Promo details
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: tokens.textSubtle,
                  }}
                >
                  Configure what doctors/patients will see on the dashboard.
                </div>
              </div>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 12,
                  fontWeight: 600,
                  color: tokens.textSubtle,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                New promo
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <LabelInput
                label="Top label"
                value={form.topLabel}
                onChange={(e) => onFieldChange("topLabel", e.target.value)}
                placeholder="MIKDOK FOKUS"
              />
              <div>
                <span
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: tokens.textSubtle,
                    marginBottom: 2,
                  }}
                >
                  Slot
                </span>
                <select
                  value={form.slot}
                  onChange={(e) => onFieldChange("slot", e.target.value)}
                  style={selectStyle}
                >
                  <option value="doctor-home-main">
                    Doctor – Home main banner
                  </option>
                  <option value="patient-home-main">
                    Patient – Home main banner
                  </option>
                  {/* add more slots later */}
                </select>
              </div>
            </div>

            <LabelInput
              label="Title"
              value={form.title}
              onChange={(e) => onFieldChange("title", e.target.value)}
              placeholder="Telehealth consultations, made easy"
            />

            <LabelInput
              label="Subtitle"
              value={form.subtitle}
              onChange={(e) => onFieldChange("subtitle", e.target.value)}
              placeholder="Explain quickly what this promo is about."
            />
            <LabelInput
  label="Image URL (300×100)"
  value={form.imageUrl}
  onChange={(e) => onFieldChange("imageUrl", e.target.value)}
  placeholder="https://…/your-image-300x100.png"
/>

            <LabelTextarea
              label="Description (optional)"
              value={form.description}
              onChange={(e) => onFieldChange("description", e.target.value)}
              placeholder="Longer optional description, detailed instructions, etc."
            />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
                gap: 10,
                marginTop: 8,
              }}
            >
              <LabelInput
                label="CTA label"
                value={form.ctaLabel}
                onChange={(e) => onFieldChange("ctaLabel", e.target.value)}
                placeholder="Open guide"
              />
              <div>
                <span
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: tokens.textSubtle,
                    marginBottom: 2,
                  }}
                >
                  Target type
                </span>
                <select
                  value={form.targetType}
                  onChange={(e) => onFieldChange("targetType", e.target.value)}
                  style={selectStyle}
                >
                  <option value="internal">Internal page (route)</option>
                  <option value="external">External URL</option>
                </select>
              </div>
            </div>

            {form.targetType === "internal" ? (
              <LabelInput
                label="Internal route (ctaPath)"
                value={form.ctaPath}
                onChange={(e) => onFieldChange("ctaPath", e.target.value)}
                placeholder="/doctor/telehealth-guide"
              />
            ) : (
              <LabelInput
                label="External URL"
                value={form.externalUrl}
                onChange={(e) => onFieldChange("externalUrl", e.target.value)}
                placeholder="https://mikdok.al/blog/telehealth"
              />
            )}

            {/* Audience + schedule */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1.3fr) minmax(0,1fr)",
                gap: 10,
                marginTop: 10,
              }}
            >
              <div>
                <span
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: tokens.textSubtle,
                    marginBottom: 4,
                  }}
                >
                  Audience
                </span>
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    fontSize: 13,
                    fontWeight: 500,
                    color: tokens.text,
                  }}
                >
                  <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <input
                      type="checkbox"
                      checked={form.audiences.doctor}
                      onChange={() => onAudienceToggle("doctor")}
                    />
                    Doctor dashboard
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <input
                      type="checkbox"
                      checked={form.audiences.patient}
                      onChange={() => onAudienceToggle("patient")}
                    />
                    Patient app
                  </label>
                </div>
                {!selectedPromoId && (
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: 11,
                      color: tokens.textSubtle,
                    }}
                  >
                    For new promos, one document will be created per audience.
                  </p>
                )}
              </div>

              <div>
                <span
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: tokens.textSubtle,
                    marginBottom: 4,
                  }}
                >
                  Schedule
                </span>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
                    gap: 6,
                  }}
                >
                  <input
                    type="date"
                    value={form.validFrom}
                    onChange={(e) =>
                      onFieldChange("validFrom", e.target.value)
                    }
                    style={inputStyle}
                  />
                  <input
                    type="date"
                    value={form.validTo}
                    onChange={(e) => onFieldChange("validTo", e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: 6,
                    gap: 8,
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 12,
                      color: tokens.textSubtle,
                      fontWeight: 500,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) =>
                        onFieldChange("active", e.target.checked)
                      }
                    />
                    Active
                  </label>
                  <LabelSmallInput
                    label="Priority"
                    value={form.priority}
                    onChange={(e) =>
                      onFieldChange("priority", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Save button */}
            <div
              style={{
                marginTop: 14,
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={handleSavePromo}
                disabled={saving}
                style={{
                  padding: "8px 16px",
                  borderRadius: 999,
                  border: "none",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  background: saving
                    ? "#d1d5db"
                    : "linear-gradient(135deg,#22c55e,#16a34a)",
                  color: "#022c22",
                  minWidth: 120,
                }}
              >
                {saving
                  ? "Saving..."
                  : selectedPromoId
                  ? "Update promo"
                  : "Create promo(s)"}
              </button>
            </div>
          </section>

          {/* Preview card */}
          <section
            style={{
              background: tokens.cardBg,
              borderRadius: tokens.radius,
              border: `1px solid ${tokens.cardBorder}`,
              boxShadow: tokens.shadow,
              padding: 16,
            }}
          >
            <div
              style={{
                marginBottom: 10,
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: tokens.text,
                  }}
                >
                  Live preview
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: tokens.textSubtle,
                  }}
                >
                  This is how it will look on the dashboard.
                </div>
              </div>
            </div>

            <div style={{ marginTop: 6 }}>
              <PromoSpot
                promo={previewPromo}
                onCtaClick={handlePreviewClick}
                onExpand={handlePreviewExpand}
                onClickTrack={handlePreviewClickTrack}
              />
            </div>
          </section>
        </div>

        {/* Dashboard: existing promos + metrics */}
        <section
          style={{
            background: tokens.cardBg,
            borderRadius: tokens.radius,
            border: `1px solid ${tokens.cardBorder}`,
            boxShadow: tokens.shadow,
            padding: 16,
          }}
        >
          <div
            style={{
              marginBottom: 10,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: tokens.text,
                }}
              >
                Published promos
              </div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: tokens.textSubtle,
                }}
              >
                Overview of all promos and their performance.
              </div>
            </div>
          </div>

          {loadingPromos ? (
            <div
              style={{
                padding: 12,
                fontSize: 13,
                color: tokens.textSubtle,
                fontWeight: 500,
              }}
            >
              Loading promos…
            </div>
          ) : promos.length === 0 ? (
            <div
              style={{
                padding: 12,
                fontSize: 13,
                color: tokens.textSubtle,
                fontWeight: 500,
              }}
            >
              No promos yet. Create your first promo above.
            </div>
          ) : (
            <div
              style={{
                overflowX: "auto",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr
                    style={{
                      borderBottom: `1px solid ${tokens.cardBorder}`,
                      background: "#f9fafb",
                    }}
                  >
                    <Th>Title</Th>
                    <Th>Audience</Th>
                    <Th>Slot</Th>
                    <Th>Active</Th>
                    <Th>Priority</Th>
                    <Th>Schedule</Th>
                    <Th>
                      Impr.
                      {loadingStats && (
                        <span style={{ marginLeft: 4 }}>…</span>
                      )}
                    </Th>
                    <Th>Views</Th>
                    <Th>Clicks</Th>
                    <Th>CTR</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {promos.map((p) => {
                    const stats = statsByPromo[p.id] || {
                      impressions: 0,
                      views: 0,
                      clicks: 0,
                      ctr: 0,
                    };
                    return (
                      <tr
                        key={p.id}
                        style={{
                          borderBottom: `1px solid ${tokens.cardBorder}`,
                        }}
                      >
                        <Td
                          style={{
                            maxWidth: 220,
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                          }}
                          title={p.title}
                        >
                          {p.title || "Untitled"}
                        </Td>
                        <Td>{p.audience || "-"}</Td>
                        <Td>{p.slot || "-"}</Td>
                        <Td>{p.active === false ? "No" : "Yes"}</Td>
                        <Td>{p.priority ?? 10}</Td>
                        <Td>
                          {p.validFrom || p.validTo
                            ? `${p.validFrom || "—"} → ${p.validTo || "—"}`
                            : "Always"}
                        </Td>
                        <Td>{stats.impressions}</Td>
                        <Td>{stats.views}</Td>
                        <Td>{stats.clicks}</Td>
                        <Td>
                          {stats.ctr > 0
                            ? `${stats.ctr.toFixed(1)}%`
                            : "0.0%"}
                        </Td>
                        <Td>
                          <button
                            type="button"
                            onClick={() => loadPromoIntoForm(p)}
                            style={{
                              padding: "4px 10px",
                              borderRadius: 999,
                              border: "1px solid #d1d5db",
                              background: "#f9fafb",
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            Edit
                          </button>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

/* Small presentational helpers */

const inputStyle = {
  width: "100%",
  borderRadius: 999,
  border: "1px solid #d1d5db",
  padding: "6px 10px",
  fontSize: 12,
  outline: "none",
};

const selectStyle = {
  ...inputStyle,
  borderRadius: 999,
  paddingRight: 26,
};

const LabelInput = ({ label, value, onChange, placeholder }) => (
  <label style={{ display: "block", marginBottom: 6 }}>
    <span
      style={{
        display: "block",
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "#6b7280",
        marginBottom: 2,
      }}
    >
      {label}
    </span>
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={inputStyle}
    />
  </label>
);

const LabelSmallInput = ({ label, value, onChange }) => (
  <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "#6b7280",
      }}
    >
      {label}
    </span>
    <input
      type="number"
      value={value}
      onChange={onChange}
      style={{
        ...inputStyle,
        width: 60,
        borderRadius: 999,
        padding: "4px 8px",
        fontSize: 11,
      }}
    />
  </label>
);

const LabelTextarea = ({ label, value, onChange, placeholder }) => (
  <label style={{ display: "block", marginTop: 4 }}>
    <span
      style={{
        display: "block",
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "#6b7280",
        marginBottom: 2,
      }}
    >
      {label}
    </span>
    <textarea
      rows={3}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        ...inputStyle,
        borderRadius: 10,
        resize: "vertical",
        minHeight: 70,
      }}
    />
  </label>
);

const Th = ({ children }) => (
  <th
    style={{
      textAlign: "left",
      padding: "6px 8px",
      fontSize: 11,
      fontWeight: 700,
      color: "#4b5563",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </th>
);

const Td = ({ children, style }) => (
  <td
    style={{
      padding: "6px 8px",
      fontSize: 12,
      color: "#111827",
      fontWeight: 500,
      ...style,
    }}
  >
    {children}
  </td>
);

export default AdminPromoDashboard;
